import type { StepPath } from '~/stores/steps-definition'
import { useTitle } from '@vueuse/core'
import { defineStore } from 'pinia'
import { loadTranslations, translate, useTranslations } from '~/composables/translate/translations'
import { useLocationStore } from '~/stores/location'
import { useReferralStore } from '~/stores/referral'
import { STEPS_RECORD } from '~/stores/steps-definition'

interface StepState {
  stepsStarted: Record<StepPath, boolean>
  currentStepPath?: StepPath
  safetyOverlayAccepted: boolean
}

export const useStepStore = defineStore('step', {
  state: (): StepState => {
    return {
      stepsStarted: {
        category: false,
        lead: false,
        coverage: false,
        options: false,
        personal: false,
      },
      currentStepPath: undefined,
      safetyOverlayAccepted: false,
    }
  },
  actions: {
    setInitialStepFrom(callerPath: StepPath): void {
      this.setInitialStepTo('category')
      if (userHasUsedBrowserNavigationArrow(this.$state, callerPath)) {
        this.currentStepPath = callerPath
      }
    },
    setInitialStepTo(stepPath: 'category' | 'lead') {
      if (!this.currentStepPath) {
        this.$patch((state: StepState) => {
          state.currentStepPath = stepPath
          state.stepsStarted[stepPath] = true
        })
        navigate(this.currentStepPath!)
      }
    },
    goToStep(stepPath: StepPath) {
      if (this.stepsStarted[stepPath]) {
        this.currentStepPath = stepPath
        navigate(stepPath)
      }
    },
    goToNextStep() {
      if (this.currentStepPath && STEPS_RECORD[this.currentStepPath].next) {
        this.$patch((state: StepState) => {
          state.currentStepPath = STEPS_RECORD[this.currentStepPath!].next!.path
          state.stepsStarted[state.currentStepPath] = true
        })
        navigate(this.currentStepPath)
      }
    },
    async reloadStepWith(language: string): Promise<void> {
      // TODO: add sentry alert
      // TODO: navigate to error page
      if (this.currentStepPath) {
        await loadTranslations(language)
        window.__ucCmp.changeLanguage(language)
        navigate(this.currentStepPath)
      }
    },
    reset() {
      const safetyOverlayAccepted = this.safetyOverlayAccepted
      this.$reset()
      this.setInitialStepTo('category')
      this.safetyOverlayAccepted = safetyOverlayAccepted
    },
  },
})

function navigate(stepPath: StepPath) {
  const locationStore = useLocationStore()
  const referralStore = useReferralStore()
  const {
    path,
    translationKey,
  } = STEPS_RECORD[stepPath]
  const language = useTranslations().language.value

  // Construct UTM parameters
  const params = new URLSearchParams()
  if (referralStore.leadGenerator) {
    params.set('utm_source', referralStore.leadGenerator)
  }

  if (referralStore.leadPlatform) {
    params.set('utm_medium', referralStore.leadPlatform)
  }

  const utmParams = params.toString() ? `?${params.toString()}` : ''

  navigateTo(`/${language}/${locationStore.city.id}/${path}${utmParams}`)

  const newTitle = `${translate(translationKey)} | ${translate('page_title_main')}`
  useTitle(newTitle)
}

function userHasUsedBrowserNavigationArrow(state: StepState, callerStep: StepPath): boolean {
  return state.currentStepPath !== callerStep && state.stepsStarted[callerStep]
}
